/**
 * What a player did over the last 7, 30 or 90 days (Xanax, refills, time played, attacks…) and two players side by side
 * — one copy for the extension's Activity lookup on profiles (apps/extension core/lookups.ts re-exports it) and the
 * website's player page (apps/web pages/lookup.ts, website roadmap S06). Pure: whoever asks Torn passes the answers in.
 *
 * Torn's personal stats come in two shapes, both read here:
 *  - v1 `/user/{id}?selections=personalstats[&stat=…&timestamp=…]`: `{ personalstats: { xantaken: 900, … } }`;
 *  - v2 `/user/{id}/personalstats?stat=…[&timestamp=…]`: `{ personalstats: [{ name: 'xantaken', value: 900, … }] }`.
 * The stat names are v1's in both (v2 takes them in `stat=`; at most 10 a call).
 */

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : typeof x === 'string' && /^-?\d+(\.\d+)?$/.test(x) ? Number(x) : null);
const str = (x: unknown): string | null => (typeof x === 'string' && x.trim() ? x.trim() : null);

/** The personal stats compared (v1 names; exactly 10, Torn's most for one call with `stat=`). */
export const ACTIVITY_STATS = ['xantaken', 'refills', 'energydrinkused', 'boostersused', 'statenhancersused', 'useractivity', 'attackswon', 'attacksassisted', 'rankedwarhits', 'alcoholused'] as const;
export type ActivityStat = (typeof ACTIVITY_STATS)[number];

export const ACTIVITY_LABELS: Record<ActivityStat, string> = {
  xantaken: 'Xanax taken',
  refills: 'Energy refills',
  energydrinkused: 'Energy cans used',
  boostersused: 'Boosters used',
  statenhancersused: 'Stat enhancers used',
  useractivity: 'Time played',
  attackswon: 'Attacks won',
  attacksassisted: 'Attacks assisted',
  rankedwarhits: 'Ranked war hits',
  alcoholused: 'Alcohol used',
};

/** The periods offered (days). */
export const ACTIVITY_DAYS = [7, 30, 90] as const;
export type ActivityDays = (typeof ACTIVITY_DAYS)[number];
export const isActivityDays = (x: unknown): x is ActivityDays => (ACTIVITY_DAYS as readonly unknown[]).includes(x);

export interface ActivityRow {
  stat: ActivityStat;
  label: string;
  /** Over the period. */
  period: number;
  /** Per day over the period. */
  perDay: number;
  /** All time. */
  total: number;
}

export interface PlayerActivity {
  name: string | null;
  level: number | null;
  /** Days old (Torn age). */
  age: number | null;
  days: number;
  rows: ActivityRow[];
  /** Energy a day from Xanax (250) and refills (150), averaged over the period. */
  extraEnergyPerDay: number;
  /** Working stats from the Hall of Fame, when it says. */
  workingStats: number | null;
  /** True when Torn had no personal stats for N days ago (e.g. a newer account): totals stand in. */
  partial: boolean;
}

/** Torn's personal stats in either shape (above) → flat `name: number` (numbers only). Nothing usable → {}. */
export function personalStatsOf(body: unknown): Record<string, number> {
  const ps = obj(body)?.personalstats ?? null;
  const out: Record<string, number> = {};
  if (Array.isArray(ps)) {
    for (const e of ps.slice(0, 400)) {
      const o = obj(e);
      const name = str(o?.name);
      const v = num(o?.value);
      if (name && /^[a-z0-9_]{1,48}$/.test(name) && v !== null) out[name] = v;
    }
    return out;
  }
  for (const [k, v] of Object.entries(obj(ps) ?? {}).slice(0, 400)) {
    const n = num(v);
    if (n !== null && /^[a-z0-9_]{1,48}$/.test(k)) out[k] = n;
  }
  return out;
}

/** The rows from the stats now and N days ago (null: Torn had nothing then — the totals stand in). */
export function activityRows(now: Readonly<Record<string, number>>, then: Readonly<Record<string, number>> | null, days: number): ActivityRow[] {
  return ACTIVITY_STATS.map((stat): ActivityRow => {
    const total = now[stat] ?? 0;
    const before = then?.[stat];
    const period = Math.max(0, total - (before ?? 0));
    return { stat, label: ACTIVITY_LABELS[stat], period, perDay: days > 0 ? period / days : 0, total };
  });
}

/** Energy a day from Xanax (250 E) and refills (150 E), averaged over the period (cans not counted). */
export const extraEnergyPerDay = (rows: readonly ActivityRow[]): number => {
  const per = (s: ActivityStat) => rows.find((r) => r.stat === s)?.perDay ?? 0;
  return Math.round(per('xantaken') * 250 + per('refills') * 150);
};

/** From Torn's answers: the profile + personal stats now, the same stats N days ago (either shape), the Hall of Fame. */
export function readPlayerActivity(now: unknown, then: unknown, days: number, hof: unknown = null): PlayerActivity {
  const cur = personalStatsOf(now);
  const old = obj(then) && obj(then)!.personalstats != null ? personalStatsOf(then) : null;
  const p = obj(now) ?? {};
  const rows = activityRows(cur, old, days);
  return {
    name: str(p.name),
    level: num(p.level),
    age: num(p.age),
    days,
    rows,
    extraEnergyPerDay: extraEnergyPerDay(rows),
    workingStats: num(obj(obj(obj(hof)?.hof)?.working_stats)?.value),
    partial: !old,
  };
}

/** "3h 12m" for a number of seconds. */
export function hoursText(seconds: number): string {
  const m = Math.round(seconds / 60);
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

/** One number of a row as it reads: time played as hours, per-day small counts with 2 decimals, the rest as counts. */
export function activityValue(stat: ActivityStat, n: number, perDay = false): string {
  if (stat === 'useractivity') return hoursText(n);
  if (perDay && n < 10) return n.toFixed(2);
  return Math.round(n).toLocaleString('en-US');
}

/** How a row's numbers read: [period, per day, all time]. */
export function activityCells(r: ActivityRow): [string, string, string] {
  return [activityValue(r.stat, r.period), activityValue(r.stat, r.perDay, true), activityValue(r.stat, r.total)];
}

/** Two players' rows side by side, with "first minus second" for the period and all time. */
export interface ActivityCompareRow {
  stat: ActivityStat;
  label: string;
  a: ActivityRow;
  b: ActivityRow;
  /** a − b over the period. */
  period: number;
  /** a − b all time. */
  total: number;
}

export function compareActivity(a: readonly ActivityRow[], b: readonly ActivityRow[]): ActivityCompareRow[] {
  const out: ActivityCompareRow[] = [];
  for (const ra of a) {
    const rb = b.find((r) => r.stat === ra.stat);
    if (rb) out.push({ stat: ra.stat, label: ra.label, a: ra, b: rb, period: ra.period - rb.period, total: ra.total - rb.total });
  }
  return out;
}

// ---------------------------------------------------------------- the website's server read (GET /api/players/:id/stats)

/** Kept on the server (memory only) for an hour: asking again within it costs no Torn call. */
export const PLAYER_STATS_KEEP_MS = 3_600_000;
/** Torn calls a minute for this, everyone together (lent keys first, else the server's own key). */
export const PLAYER_STATS_PER_MINUTE = 10;
/** Torn calls a minute for one asker (a player with a period is 2 calls). */
export const PLAYER_STATS_PER_ASKER = 4;

/** One read of a player's activity stats: now, and N days ago. */
export interface PlayerStatsRead {
  /** Unix ms Torn answered. */
  at: number;
  stats: Record<string, number>;
}

export interface PlayerStatsAnswer {
  id: number;
  days: ActivityDays;
  /** Null: nothing kept (asked with `kept=1`), or Torn couldn't be asked. */
  now: PlayerStatsRead | null;
  /** Null: not read yet, or Torn had nothing that far back (a newer account). */
  then: PlayerStatsRead | null;
  /** Calls this answer cost (0: all from what was kept). */
  calls: number;
  /** Why there's nothing (in plain words), when there isn't. */
  problem?: string;
}
